import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Camera, Upload, Download, Plus, Loader2, Lock } from "lucide-react";
import { useRole } from "@/lib/roles";
import { toast } from "sonner";
import { PlaceholderThumb } from "@/components/photos/AuthImage";
import { PhotoCard } from "@/components/photos/PhotoCard";
import { PhotoViewerModal } from "@/components/photos/PhotoViewerModal";
import { DeletePhotoDialog } from "@/components/photos/DeletePhotoDialog";

/**
 * PhotosSection — Photo evidence for a test record.
 * Images live in SharePoint; the app stores metadata + references only.
 */
export default function PhotosSection({ testRecordId = null, locked = false, onRequireSave = null }) {
  const { canWrite, canDelete, isAdmin } = useRole();

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);

  const canAddPhotos = canWrite && !locked;
  const canDeletePhotos = (canDelete || canWrite) && !locked;

  const fetchPhotos = useCallback(async (id) => {
    if (!id) {
      setPhotos([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/tests/${id}/photos`);
      setPhotos(res.data || []);
    } catch (err) {
      // 404 on a brand-new record id is fine; otherwise surface it
      if (err?.response?.status !== 404) toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
    fetchPhotos(testRecordId);
  }, [testRecordId, fetchPhotos]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    // Fail-safe pre-save: a photo must always attach to a saved record.
    let targetId = testRecordId;
    if (!targetId) {
      if (!onRequireSave) {
        toast.error("Please save the test record before adding photos.");
        return;
      }
      try {
        targetId = await onRequireSave();
      } catch (e) {
        toast.error(e?.message || "Could not save the record — photo not uploaded.");
        return;
      }
      if (!targetId) {
        toast.error("Could not save the record — photo not uploaded.");
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    let okCount = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/tests/${targetId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        okCount += 1;
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    setUploading(false);
    setUploadProgress(0);
    if (okCount) {
      toast.success(okCount > 1 ? `${okCount} photos uploaded` : "Photo uploaded");
    }
    await fetchPhotos(targetId);
  };

  const onCameraPick = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };
  const onUploadPick = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/photos/${pendingDelete.id}`);
      if (viewPhoto && viewPhoto.id === pendingDelete.id) setViewPhoto(null);
      toast.success("Photo deleted");
      await fetchPhotos(testRecordId);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setPendingDelete(null);
  };

  const downloadPhoto = async (photo) => {
    try {
      const r = await api.get(`/photos/${photo.id}/content`, {
        params: { download: true },
        responseType: "blob",
      });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = photo.original_file_name || "photo.jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadAll = async () => {
    if (!photos.length || !testRecordId) return;
    setDownloadingAll(true);
    try {
      const r = await api.get(`/tests/${testRecordId}/photos/download-all`, {
        responseType: "blob",
      });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = `TestRecord-${(testRecordId || "").slice(0, 8)}-Photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setDownloadingAll(false);
  };

  return (
    <section data-testid="photos-section" className="mt-10 bg-white border border-[#33424f]/20">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onCameraPick}
        data-testid="photo-camera-input"
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        onChange={onUploadPick}
        data-testid="photo-upload-input"
      />

      {/* Header */}
      <div className="border-b border-[#33424f]/20 p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#39678f] subtitle">/ Evidence</div>
          <h3 className="font-display font-bold text-lg mt-1 flex items-center gap-2">
            Photos
            {photos.length > 0 && (
              <span className="text-xs font-body font-normal text-[#7a6f57]">({photos.length})</span>
            )}
            {locked && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#7a6f57] bg-[#f0ece3] border border-[#33424f]/15 px-2 py-1">
                <Lock className="w-3 h-3" /> Locked · view only
              </span>
            )}
          </h3>
          <p className="text-xs text-[#50493b] mt-1 max-w-xl">
            Optional. Attach photographic evidence to this test record. Files are stored in
            SharePoint; this app keeps only the reference + metadata.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              onClick={downloadAll}
              disabled={!photos.length || downloadingAll}
              data-testid="download-all-photos-btn"
              className="h-12 px-4 border-[#33424f]/30 text-[#33424f] subtitle uppercase tracking-wider text-xs disabled:opacity-40"
            >
              {downloadingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download all
            </Button>
          )}

          {canAddPhotos && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  data-testid="add-photo-btn"
                  disabled={uploading}
                  className="h-12 px-5 bg-[#ea580c] hover:bg-[#c2410c] text-white subtitle uppercase tracking-wider text-xs"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add photo
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="take-photo-option"
                  className="py-3 text-sm cursor-pointer"
                >
                  <Camera className="w-5 h-5 mr-3 text-[#39678f]" /> Take photo
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => uploadInputRef.current?.click()}
                  data-testid="upload-photo-option"
                  className="py-3 text-sm cursor-pointer"
                >
                  <Upload className="w-5 h-5 mr-3 text-[#39678f]" /> Upload existing photo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        {!testRecordId && canAddPhotos && (
          <div
            data-testid="photos-presave-hint"
            className="mb-5 border border-[#39678f]/30 bg-[#eef3f8] text-[#2c5272] text-xs font-body p-3"
          >
            This is a new record. Adding a photo will first save the record (once the required
            fields are completed), then upload the photo and link it automatically.
          </div>
        )}

        {uploading && (
          <div data-testid="upload-progress" className="mb-5 border border-[#33424f]/20 bg-[#f5f1ec] p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#50493b] subtitle mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#ea580c]" />
              Uploading… {uploadProgress}%
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-sm text-[#7a6f57]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#9a917f]" />
            Loading photos…
          </div>
        ) : photos.length === 0 && !uploading ? (
          <div
            data-testid="photos-empty-state"
            className="border border-dashed border-[#33424f]/25 bg-[#faf8f4] py-12 px-6 text-center"
          >
            <PlaceholderThumb className="w-16 h-16 mx-auto mb-4 rounded" />
            <p className="font-body text-sm text-[#50493b]">No photos have been added to this test record.</p>
            {canAddPhotos && (
              <Button
                type="button"
                onClick={() => uploadInputRef.current?.click()}
                className="mt-4 h-11 px-5 bg-[#ea580c] hover:bg-[#c2410c] text-white subtitle uppercase tracking-wider text-xs"
              >
                <Plus className="w-4 h-4 mr-2" /> Add photo
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {photos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                onView={setViewPhoto}
                onDownload={downloadPhoto}
                onDelete={setPendingDelete}
                canDeletePhotos={canDeletePhotos}
              />
            ))}
          </div>
        )}
      </div>

      <PhotoViewerModal
        viewPhoto={viewPhoto}
        onOpenChange={(o) => !o && setViewPhoto(null)}
        onDownload={downloadPhoto}
        onDelete={setPendingDelete}
        canDeletePhotos={canDeletePhotos}
      />

      <DeletePhotoDialog
        pendingDelete={pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </section>
  );
}
